// Configuración de API para desarrollo y producción

const API_CONFIG = {
  // URL base de la API - Detectar automáticamente el entorno
  // En desarrollo, usar localhost. En producción, usar Railway.
  BASE_URL: process.env.REACT_APP_API_URL || 
    (process.env.NODE_ENV === 'development' 
      ? 'http://localhost:3001' 
      : 'https://worker-production-86fa.up.railway.app'),
  
  // Endpoints
  ENDPOINTS: {
    // Monitoreo
    COLLECTIBLES_DATA: '/api/monitoring/raw',
    MATCHES: '/api/monitoring/matches',
    MATCH_DETAIL: '/api/monitoring/match',
    STATS: '/api/monitoring/stats',
    
    // Usuarios
    USER_PROFILE: '/api/users/profile',
    UPDATE_CONFIG: '/api/users/config',
    ALERTS_HISTORY: '/api/users/alerts',
    
    // Autenticación
    VERIFY_TOKEN: '/api/auth/verify',
    
    // Health check
    HEALTH: '/health'
  }
};

// Función para construir URLs completas
export const buildApiUrl = (endpoint, params = {}) => {
  const base = (API_CONFIG.BASE_URL || '').replace(/\/+$/g, '');
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  let url = `${base}${path}`;
  
  // Agregar parámetros de query si existen
  if (Object.keys(params).length > 0) {
    const queryParams = new URLSearchParams(params);
    url += `?${queryParams.toString()}`;
  }
  
  return url;
};

// Función para renovar token
async function refreshAuthToken() {
  try {
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (user) {
      const token = await user.getIdToken(true); // Force refresh
      localStorage.setItem('authToken', token);

      return token;
    }
    return null;
  } catch (error) {
    console.error('❌ Error renovando token:', error);
    return null;
  }
}

// Función para hacer requests a la API
export const apiRequest = async (endpoint, options = {}) => {
  const url = buildApiUrl(endpoint, options.params);
  
  const config = {
    method: options.method || 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    }
  };
  
  // Agregar token de autenticación si existe
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Manejar el body correctamente
  if (options.body && (options.method === 'POST' || options.method === 'PUT' || options.method === 'PATCH')) {
    config.body = JSON.stringify(options.body);
  }
  
  // Agregar otras opciones (excepto body que ya se manejó)
  const { body, ...otherOptions } = options;
  Object.assign(config, otherOptions);
  
  try {

    
    const response = await fetch(url, config);
    
    // Si el token expiró, intentar renovarlo y reintentar
    if (response.status === 401 || response.status === 403) {
      console.log('🔄 Token expirado, intentando renovar...');
      
      const newToken = await refreshAuthToken();
      if (newToken) {
        console.log('✅ Token renovado, reintentando petición...');
        // Reintentar con el nuevo token
        config.headers.Authorization = `Bearer ${newToken}`;
        const retryResponse = await fetch(url, config);
        
        if (!retryResponse.ok) {
          const errorText = await retryResponse.text();
          let errorData;
          try {
            errorData = JSON.parse(errorText);
          } catch {
            errorData = { error: errorText };
          }
          throw new Error(errorData.error || `HTTP error! status: ${retryResponse.status}`);
        }
        
        return await retryResponse.json();
      } else {
        // Si no se pudo renovar el token, redirigir al login
        console.log('❌ No se pudo renovar token, redirigiendo al login...');
        localStorage.removeItem('authToken');
        window.location.href = '/login';
        throw new Error('Token expirado y no se pudo renovar');
      }
    }
    
    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText };
      }
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('API Request Error:', error);
    throw error;
  }
};

export default API_CONFIG;
